import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, FileArchive, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * `/admin/affiliate-paperwork` — everything approved affiliates have uploaded,
 * grouped by preparer so Di'Meka can process funded returns without digging
 * through emails.
 */
export default function AdminAffiliatePaperwork() {
  const [uploads, setUploads] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/affiliate/admin/paperwork")
      .then(r => setUploads(r.data.uploads || []))
      .catch(() => toast.error("Could not load paperwork"));
  }, []);

  const grouped = useMemo(() => {
    if (!uploads) return {};
    const q = filter.trim().toLowerCase();
    const list = q
      ? uploads.filter(u =>
          (u.affiliate_name || "").toLowerCase().includes(q) ||
          (u.affiliate_email || "").toLowerCase().includes(q) ||
          (u.week_of || "").includes(q) ||
          (u.filename || "").toLowerCase().includes(q))
      : uploads;
    const by = {};
    for (const u of list) {
      const k = u.affiliate_id;
      if (!by[k]) by[k] = { name: u.affiliate_name, email: u.affiliate_email, rows: [] };
      by[k].rows.push(u);
    }
    return by;
  }, [uploads, filter]);

  if (!uploads) return <Layout><div className="p-10 text-muted-foreground">Loading…</div></Layout>;

  const entries = Object.entries(grouped);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12" data-testid="admin-paperwork-page">
        <Link to="/admin/affiliate-contracts" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={14} /> Back to contracts</Link>
        <div className="flex items-center gap-3 mb-2">
          <FileArchive size={22} className="text-primary" />
          <div className="label-caps">Weekly uploads</div>
        </div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-2">Paperwork.</h1>
        <p className="text-muted-foreground mb-8">{uploads.length} file{uploads.length === 1 ? "" : "s"} from {entries.length} preparer{entries.length === 1 ? "" : "s"}.</p>

        <div className="mb-6 relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search preparer, email, week, filename…" value={filter} onChange={e => setFilter(e.target.value)} className="pl-8" data-testid="admin-paperwork-search" />
        </div>

        {entries.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">No paperwork uploaded yet.</Card>
        ) : (
          <div className="space-y-4" data-testid="admin-paperwork-groups">
            {entries.map(([affId, g]) => (
              <Card key={affId} className="p-5" data-testid={`admin-paperwork-group-${affId}`}>
                <div className="flex items-baseline justify-between mb-3 pb-3 border-b border-border">
                  <div>
                    <div className="font-serif text-2xl">{g.name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{g.email}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{g.rows.length} file{g.rows.length === 1 ? "" : "s"}</div>
                </div>
                <div className="space-y-2">
                  {g.rows.map(u => (
                    <div key={u.id} className="flex items-center gap-3 text-sm p-2 rounded-md hover:bg-secondary/50">
                      <FileArchive size={14} className="text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{u.filename}</div>
                        <div className="text-[10px] text-muted-foreground">
                          Week of {u.week_of || "—"} · {new Date(u.created_at).toLocaleString()}
                          {u.note ? ` · ${u.note}` : ""}
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate max-w-xs">…{u.storage_path.slice(-30)}</div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
